import { useState, useEffect, useCallback, useMemo, forwardRef, /* useRef */ } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Users,
  Trophy,
  Medal,
  Copy,
  Check,
  Crown,
  X,
  Trash2,
  Settings,
  Play,
  UserPlus,
  UserMinus,
  UserCheck,
  UserX,
  Info,
  Shield,
  ShieldOff,
  ChevronDown,
  Loader2,
  AlertTriangle,
  Ban,
  Clock,
  History,
  RefreshCw,
  TrendingUp,
  Swords,
  ArrowUpDown,
  BarChart3,
  Gift,
  Plus,
  Pencil,
  CalendarClock,
  QrCode,
  Banknote,
  AlertCircle,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppShell } from '../components/layout/AppShell';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { RankingGroupImage } from '../components/rankings/RankingGroupImage';
import { RankingShareButton } from '../components/rankings/RankingShareButton';
import { RankingEvolutionView } from '../components/rankings/RankingEvolutionView';
import { FeedbackSurveyModal } from '../components/common/FeedbackSurveyModal';
import { WhatsAppMessageActions } from '../components/common/WhatsAppMessageActions';
import {
  ScoringRulesFields,
  ScoringRulesSummary,
} from '../components/rankings/ScoringRulesFields';
import { PlayerStatRow, PlayerStatHeader } from '../components/stats/PlayerStatRow';
import {
  ScheduleModeOption,
  WeeklyScheduleFields,
  formatScheduleMoment,
  deviceTimeZone,
} from '../components/rankings/WeeklyScheduleFields';
import { AutoDraftCard, AutoDraftEditModal } from '../components/rankings/AutoDraftSettings';
import { api } from '../services/api';
import { RANKING_LIMITS } from '../config/rankingLimits';
import { trackEvent } from '../utils/analytics';
import { canonicalOrigin } from '../utils/canonicalOrigin';
import { readPayloadCache, writePayloadCache } from '../utils/payloadCache';
import type {
  RankingDetail,
  RankingPlayer,
  RankingRoundSummary,
  RankingLeaderboardMetric,
  RankingPartialStandingsData,
  PlayerHistoryEntry,
  PlayerHistoryData,
  RankingBonus,
  RankingDashboardData,
  ScoringRules,
  MyInvoice,
} from '../types';
import {
  compareStanding,
  leaderboardPosition,
  metricNumber,
  partialDiscardsApply,
  withPartialStandings,
} from '../utils/leaderboard';

/** 'YYYY-MM-DD' → 'DD/MM/YYYY', without going through Date (no timezone shift). */
function formatBonusDate(date: string): string {
  const [year, month, day] = date.split('-');
  return day && month && year ? `${day}/${month}/${year}` : date;
}

function formatBRLCents(cents: number | null): string {
  return ((cents ?? 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Device preferences — unversioned, they must survive a deploy.
function readLS<T>(key: string): T | null {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) as T : null; } catch { return null; }
}
function writeLS<T>(key: string, data: T): void {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch { /* full or unavailable */ }
}

// localStorage-backed cache for PWA offline support + navigation speed
export const rankingCache = {
  get(id: string): RankingDetail | null { return readPayloadCache<RankingDetail>(`bt_ranking_detail_${id}`); },
  set(id: string, data: RankingDetail): void { writePayloadCache(`bt_ranking_detail_${id}`, data); },
  clear(id: string): void { try { localStorage.removeItem(`bt_ranking_detail_${id}`); } catch { /* unavailable */ } },
};
const playerHistoryCache = new Map<string, PlayerHistoryData>();
const rankingDashboardCache = new Map<string, RankingDashboardData>();

const LEADERBOARD_METRICS: Array<{ value: RankingLeaderboardMetric; label: string }> = [
  { value: 'total_points', label: 'Pontos' },
  { value: 'matches_won', label: 'Vitórias' },
  { value: 'win_rate', label: '% Vitórias' },
  { value: 'games_won', label: 'Games (feitos)' },
  { value: 'game_balance', label: 'Games (saldo)' },
  { value: 'matches_played', label: 'Partidas' },
  { value: 'rounds_played', label: 'Rodadas' },
];

// "Considerar descartes" toggle on the Classificação tab — a device preference.
const PARTIAL_DISCARDS_LS_KEY = 'btv_leaderboard_partial_discards';

// "Suas estatísticas" panel — hidden for now: the dedicated /stats page covers
// it. Flip back to true to bring the expandable card (and its survey) back.
const SHOW_RANKING_STATS_PANEL = false;

// One-time feedback survey for the "Suas estatísticas" panel.
const STATS_FEEDBACK_FEATURE = 'ranking_stats_panel';
const STATS_FEEDBACK_LS_KEY = `btv_feedback_done_${STATS_FEEDBACK_FEATURE}`;
const STATS_FEEDBACK_RATINGS = [
  { value: 'useful', emoji: '🤩', label: 'Muito' },
  { value: 'somewhat', emoji: '🙂', label: 'Mais ou menos' },
  { value: 'unused', emoji: '😐', label: 'Não uso' },
];

type Tab = 'leaderboard' | 'rounds' | 'players' | 'settings' | 'info';

export function RankingDetailPage() {
  const navigate = useNavigate();
  const { rankingId } = useParams<{ rankingId: string }>();
  const { subscriber } = useAuth();
  const cached = rankingId ? rankingCache.get(rankingId) : null;
  /**
   * Do we already know this player is awaiting payment?
   *
   * Set by the join hand-off and remembered per ranking, because waiting for a
   * PIX is exactly when people reload. Without it the first paint is the full
   * ranking skeleton — the page they have not paid to see.
   */
  const expectsPayment =
    cached?.myStatus === 'pending_payment' ||
    (rankingId ? sessionStorage.getItem(`bt_awaiting_payment_${rankingId}`) === '1' : false);
  const [ranking, setRanking] = useState<RankingDetail | null>(cached);
  const [isLoading, setIsLoading] = useState(!cached);
  const [didRefresh, setDidRefresh] = useState(false);
  // Whether myStatus has been confirmed against the server in this mount.
  // The cached ranking may carry a stale active/inactive value, so the
  // participation switch stays in its loading state until this is true.
  const [statusVerified, setStatusVerified] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('leaderboard');
  const [showStats, setShowStats] = useState(false);
  // One-time feedback survey for the stats panel. localStorage is the fast
  // path; the server is the source of truth (synced on mount below).
  const [statsFeedbackDone, setStatsFeedbackDone] = useState<boolean>(
    () => readLS<boolean>(STATS_FEEDBACK_LS_KEY) === true
  );
  const [showStatsSurvey, setShowStatsSurvey] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<RankingLeaderboardMetric>('total_points');
  const [partialDiscardsOn, setPartialDiscardsOn] = useState<boolean>(
    () => readLS<boolean>(PARTIAL_DISCARDS_LS_KEY) === true
  );
  const [partialStandings, setPartialStandings] = useState<RankingPartialStandingsData | null>(null);
  const [partialLoading, setPartialLoading] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Modals
  const [historyPlayer, setHistoryPlayer] = useState<RankingPlayer | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [showScoringEdit, setShowScoringEdit] = useState(false);
  const [showActivationEdit, setShowActivationEdit] = useState(false);
  const [showNewSeason, setShowNewSeason] = useState(false);
  const [showEvolution, setShowEvolution] = useState(false);
  const [showSeasonDropdown, setShowSeasonDropdown] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadRanking = useCallback(async () => {
    if (!rankingId) return;
    const hadCached = !!rankingCache.get(rankingId);
    try {
      const response = await api.rankings.detail(rankingId);
      if (response.success && response.data) {
        rankingCache.set(rankingId, response.data);
        // Remember the payment wait so a reload skips the ranking skeleton.
        if (response.data.myStatus === 'pending_payment') {
          sessionStorage.setItem(`bt_awaiting_payment_${rankingId}`, '1');
        } else {
          sessionStorage.removeItem(`bt_awaiting_payment_${rankingId}`);
        }
        setRanking(response.data);
        setStatusVerified(true);
        if (hadCached) {
          setDidRefresh(true);
          setTimeout(() => setDidRefresh(false), 600);
        }
      } else if (response.error?.code === 'NOT_MEMBER' || response.error?.code === 'NOT_FOUND') {
        // Deleted (or no longer ours): drop the cached copy, otherwise the
        // stale detail keeps rendering on every visit to this URL.
        rankingCache.clear(rankingId);
        setRanking(null);
        navigate('/rankings', { replace: true });
      }
    } catch (error) {
      console.error('Failed to load ranking:', error);
    }
  }, [rankingId, navigate]);

  useEffect(() => {
    if (!rankingId) return;
    if (cached) {
      // Silently refresh in the background
      loadRanking().then(() => setIsLoading(false));
    } else {
      setIsLoading(true);
      loadRanking().then(() => setIsLoading(false));
    }
  }, [rankingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync the stats-feedback flag from the server (covers new devices/browsers
  // where the localStorage fast-path flag isn't set yet).
  useEffect(() => {
    if (!SHOW_RANKING_STATS_PANEL || statsFeedbackDone) return;
    let cancelled = false;
    api.vision.getFeedbackStatus(STATS_FEEDBACK_FEATURE).then((res) => {
      if (!cancelled && res.success && res.data?.submitted) {
        writeLS(STATS_FEEDBACK_LS_KEY, true);
        setStatsFeedbackDone(true);
      }
    }).catch(() => { /* leave unset → survey shows on first open */ });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await loadRanking();
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, loadRanking]);

  const isAdmin = ranking?.myRole === 'admin';
  const isCreator = ranking?.isCreator === true;

  const activePlayers = useMemo(() =>
    (ranking?.players || []).filter(p => p.status === 'active'),
    [ranking?.players]
  );

  const pendingPlayers = useMemo(() =>
    (ranking?.players || []).filter(p => p.status === 'pending'),
    [ranking?.players]
  );

  const pendingPaymentPlayers = useMemo(() =>
    (ranking?.players || []).filter(p => p.status === 'pending_payment'),
    [ranking?.players]
  );

  const inactivePlayers = useMemo(() =>
    (ranking?.players || []).filter(p => p.status === 'inactive'),
    [ranking?.players]
  );

  const participatingPlayers = useMemo(() =>
    (ranking?.players || []).filter(p => p.status === 'active' || p.status === 'inactive'),
    [ranking?.players]
  );

  // "Considerar descartes": the standings as if the season ended today — with
  // N rounds held, each player keeps their best N − discards (a round in
  // progress counts toward a player's N only once they have a result in it).
  // Only offered while it differs from the official standings, and computed by
  // the server (same discard rule and tie-break), fetched only while the
  // toggle is on.
  const partialAvailable = useMemo(() => {
    if (!ranking) return false;
    const rounds = ranking.rounds || [];
    const completed = rounds.filter((r) => r.status === 'completed').length;
    const started = rounds.filter((r) => r.status === 'in_progress' || r.status === 'completed').length;
    return partialDiscardsApply(ranking.totalRounds, ranking.discardRounds, completed, started);
  }, [ranking]);
  const showPartialStandings = partialDiscardsOn && partialAvailable;

  useEffect(() => {
    if (!showPartialStandings || !rankingId) return;
    let cancelled = false;
    setPartialLoading(true);
    api.rankings.partialStandings(rankingId)
      .then((res) => {
        if (!cancelled && res.success && res.data) setPartialStandings(res.data);
      })
      .catch((error) => console.error('Failed to load partial standings:', error))
      .finally(() => {
        if (!cancelled) setPartialLoading(false);
      });
    return () => { cancelled = true; };
    // Refetch whenever the ranking itself is reloaded (a new score, a refresh).
  }, [showPartialStandings, rankingId, ranking?.players]);

  const togglePartialDiscards = () => {
    const next = !partialDiscardsOn;
    setPartialDiscardsOn(next);
    writeLS(PARTIAL_DISCARDS_LS_KEY, next);
  };

  // The Pontos order is the server's (points → wins → games balance →
  // head-to-head, stored per player) — never re-derived here. Every other
  // metric sorts by its own value and falls back to that order on ties.
  const sortLeaderboard = useCallback((players: RankingPlayer[]) => {
    return [...players].sort((a, b) => {
      if (selectedMetric !== 'total_points') {
        const diff = metricNumber(b, selectedMetric) - metricNumber(a, selectedMetric);
        if (diff !== 0) return diff;
      }
      return compareStanding(a, b);
    });
  }, [selectedMetric]);

  // The official standings — what the share card and everything outside the
  // Classificação list use, whatever the toggle says.
  const leaderboard = useMemo(
    () => sortLeaderboard(participatingPlayers),
    [participatingPlayers, sortLeaderboard]
  );

  const displayedLeaderboard = useMemo(() => {
    if (!showPartialStandings || partialStandings?.countingLimit == null) return leaderboard;
    return sortLeaderboard(withPartialStandings(participatingPlayers, partialStandings));
  }, [showPartialStandings, partialStandings, leaderboard, participatingPlayers, sortLeaderboard]);

  const handleShareInvite = async () => {
    if (!ranking) return;
    const inviteUrl = `${canonicalOrigin()}/rankings/join/${ranking.inviteCode}`;
    const shareText = `Entre no ranking "${ranking.name}" no BT Vision! Código: ${ranking.inviteCode}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: `Ranking ${ranking.name}`, text: shareText, url: inviteUrl });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') copyInviteCode();
      }
    } else {
      copyInviteCode();
    }
  };

  const copyInviteCode = () => {
    if (!ranking) return;
    navigator.clipboard.writeText(`${canonicalOrigin()}/rankings/join/${ranking.inviteCode}`);
    setCopiedInvite(true);
    setTimeout(() => setCopiedInvite(false), 2000);
  };

  const handlePlayerAction = async (playerId: string, action: string) => {
    if (!rankingId) return;
    setActionLoading(`${action}-${playerId}`);
    try {
      let response;
      switch (action) {
        case 'approve': response = await api.rankings.approvePlayer(rankingId, playerId); break;
        case 'reject': response = await api.rankings.rejectPlayer(rankingId, playerId); break;
        case 'toggle': response = await api.rankings.togglePlayer(rankingId, playerId); break;
        case 'remove': response = await api.rankings.removePlayer(rankingId, playerId); break;
        case 'promote': response = await api.rankings.promotePlayer(rankingId, playerId); break;
        case 'demote': response = await api.rankings.demotePlayer(rankingId, playerId); break;
        case 'mark-paid': response = await api.rankings.markPaid(rankingId, playerId); break;
        case 'exempt': response = await api.rankings.exemptPlayer(rankingId, playerId); break;
        default: return;
      }
      if (response?.success) await loadRanking();
    } catch (error) {
      console.error(`Failed to ${action} player:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDraftRound = async () => {
    if (!rankingId) return;
    setActionLoading('draft');
    try {
      const response = await api.rankings.draftRound(rankingId);
      if (response.success) {
        await loadRanking();
      } else {
        alert(response.error?.message || 'Erro ao criar rodada');
        await loadRanking();
      }
    } catch (error) {
      console.error('Failed to draft round:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleFinish = async () => {
    if (!rankingId) return;
    setActionLoading('finish');
    try {
      const response = await api.rankings.finish(rankingId);
      if (response.success) {
        await loadRanking();
        setShowFinishConfirm(false);
      }
    } catch (error) {
      console.error('Failed to finish ranking:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async () => {
    if (!rankingId) return;
    setActionLoading('reactivate');
    try {
      const response = await api.rankings.reactivate(rankingId);
      if (response.success) await loadRanking();
    } catch (error) {
      console.error('Failed to reactivate ranking:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!rankingId) return;
    setIsDeleting(true);
    try {
      const response = await api.rankings.delete(rankingId);
      if (response.success) {
        rankingCache.clear(rankingId);
        navigate('/rankings');
      }
    } catch (error) {
      console.error('Failed to delete ranking:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Someone awaiting payment is only ever going to see the PIX screen. The
  // skeleton below is the ranking's own silhouette — tab bar, leaderboard rows
  // — so showing it to them flashes a page they have not paid to see. Wait on
  // a payment-shaped placeholder instead.
  if (isLoading && expectsPayment) {
    return (
      <AppShell>
        <div className="space-y-5 max-w-lg mx-auto">
          <div className="h-4 w-16 bg-white/10 rounded animate-pulse" />
          <div className="space-y-2">
            <div className="h-6 w-48 bg-white/10 rounded animate-pulse" />
          </div>
          <div className="glass-card rounded-2xl p-5">
            <div className="flex flex-col items-center py-12">
              <Loader2 className="w-8 h-8 text-turquoise animate-spin" />
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  if (isLoading) {
    return (
      <AppShell>
        <div className="space-y-5 max-w-lg mx-auto">
          <div className="h-4 w-16 bg-white/10 rounded animate-pulse" />
          <div className="space-y-2">
            <div className="h-6 w-48 bg-white/10 rounded animate-pulse" />
            <div className="h-4 w-32 bg-white/10 rounded animate-pulse" />
          </div>
          <div className="glass-card rounded-2xl p-5 animate-pulse">
            <div className="h-6 w-24 bg-white/10 rounded" />
          </div>
          <div className="glass-card rounded-2xl p-3 animate-pulse">
            <div className="flex gap-2">
              <div className="h-9 w-20 bg-white/10 rounded-lg" />
              <div className="h-9 w-16 bg-white/10 rounded-lg" />
              <div className="h-9 w-24 bg-white/10 rounded-lg" />
            </div>
          </div>
          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="divide-y divide-white/5">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
                  <div className="w-6 h-5 bg-white/10 rounded shrink-0" />
                  <div className="w-9 h-9 rounded-full bg-white/10 shrink-0" />
                  <div className="flex-1"><div className="h-4 w-24 bg-white/10 rounded" /></div>
                  <div className="h-5 w-12 bg-white/10 rounded shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!ranking) {
    return (
      <AppShell>
        <div className="text-center py-20">
          <p className="text-white/50">Ranking não encontrado</p>
          <Button onClick={() => navigate('/rankings')} variant="secondary" className="mt-4">
            Voltar para rankings
          </Button>
        </div>
      </AppShell>
    );
  }

  // Awaiting payment: full-page PIX screen (content is blocked server-side).
  if (ranking.myStatus === 'pending_payment' && rankingId) {
    return (
      <AppShell>
        <div className="space-y-5 max-w-lg mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => navigate('/rankings')}
              className="flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-3 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Rankings
            </button>

            <div className="flex items-center gap-3">
              <RankingGroupImage imageUrl={ranking.imageUrl} size="md" />
              <div className="flex-1 min-w-0">
                <h1 className="font-display text-xl font-bold text-white truncate">
                  {ranking.name}
                </h1>
              </div>
            </div>
          </motion.div>

          <PaymentScreen rankingId={rankingId} onPaid={loadRanking} />
        </div>
      </AppShell>
    );
  }

  const isPending = ranking.myStatus === 'pending';

  if (isPending) {
    return (
      <AppShell>
        <div className="space-y-5 max-w-lg mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => navigate('/rankings')}
              className="flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-3 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Rankings
            </button>

            <div className="flex items-center gap-3">
              <RankingGroupImage imageUrl={ranking.imageUrl} size="md" />
              <div className="flex-1 min-w-0">
                <h1 className="font-display text-xl font-bold text-white truncate">
                  {ranking.name}
                </h1>
              </div>
            </div>
          </motion.div>

          <Card delay={0.1}>
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-16 h-16 rounded-full bg-sunny/10 flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-sunny" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Participação em análise</h2>
              <p className="text-sm text-white/50 max-w-xs">
                Sua solicitação para entrar neste ranking está sendo analisada. Um administrador poderá aprová-la em breve.
              </p>
            </div>
          </Card>
        </div>
      </AppShell>
    );
  }

  const tabs: Array<{ key: Tab; label: string; badge?: number }> = [
    { key: 'leaderboard', label: 'Classificação' },
    { key: 'rounds', label: 'Rodadas', badge: (ranking.myPendingMatchCount + ranking.disputedMatchCount) || undefined },
    { key: 'players', label: 'Jogadores', badge: isAdmin ? ((pendingPlayers.length + pendingPaymentPlayers.length) || undefined) : undefined },
    ...(isAdmin
      ? [{ key: 'settings' as Tab, label: 'Config' }]
      : [{ key: 'info' as Tab, label: 'Info' }]),
  ];

  const switchLoading = !statusVerified || actionLoading === `toggle-${ranking.myPlayerId}`;

  return (
    <AppShell
      headerRightElement={
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-2 text-white/70 hover:text-turquoise hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      }
    >
      <div className={`space-y-5 max-w-lg mx-auto rounded-xl ${didRefresh ? 'animate-data-refresh' : ''}`}>
        {/* Back + Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate('/rankings')}
              className="flex items-center gap-1.5 text-white/50 hover:text-white text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Rankings
            </button>

            {/* Season dropdown */}
            {ranking.seasons.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setShowSeasonDropdown(!showSeasonDropdown)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/10 text-xs text-white/70 hover:bg-white/20 transition-colors"
                >
                  {ranking.subtitle}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showSeasonDropdown && (
                  <div className="absolute right-0 top-full mt-1 w-48 glass-card rounded-xl overflow-hidden z-10">
                    {ranking.seasons.map((season) => (
                      <button
                        key={season.id}
                        onClick={() => {
                          setShowSeasonDropdown(false);
                          if (season.id !== rankingId) navigate(`/rankings/${season.id}`);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${season.id === rankingId
                          ? 'bg-turquoise/10 text-turquoise'
                          : 'text-white/70 hover:bg-white/10'
                          }`}
                      >
                        {season.subtitle}
                        {season.status === 'finished' && (
                          <span className="ml-1 text-[10px] text-white/40">encerrado</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <RankingGroupImage
              imageUrl={ranking.imageUrl}
              size="md"
              editable={isAdmin}
              onImageSelected={async (base64) => {
                const res = await api.rankings.uploadGroupImage(rankingId!, base64);
                if (res.success) {
                  await loadRanking();
                } else {
                  alert(res.error?.message || 'Erro ao enviar imagem');
                }
              }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-display text-xl font-bold text-white truncate">
                  {ranking.name}
                </h1>
                {ranking.status === 'finished' && (
                  <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/10 text-white/50">
                    Encerrado
                  </span>
                )}
              </div>
              <p className="text-xs text-white/40 mt-0.5 mb-1">
                {ranking.subtitle}
              </p>
              <p className="text-sm text-white/50 flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {participatingPlayers.length} jogador{participatingPlayers.length !== 1 ? 'es' : ''}
              </p>
            </div>

            {/* Active/Inactive toggle */}
            {ranking.myPlayerId && ranking.status === 'active' && (ranking.myStatus === 'active' || ranking.myStatus === 'inactive') && (
              <button
                onClick={() => handlePlayerAction(ranking.myPlayerId!, 'toggle')}
                disabled={actionLoading !== null || !statusVerified}
                className="shrink-0 flex flex-col items-center gap-1 disabled:opacity-50"
                title={switchLoading ? 'Carregando...' : ranking.myStatus === 'active' ? 'Desativar participação' : 'Ativar participação'}
              >
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${switchLoading ? 'bg-white/10' :
                  ranking.myStatus === 'active' ? 'bg-turquoise' : 'bg-white/20'
                  }`}>
                  {switchLoading ? (
                    <Loader2 className="w-4 h-4 text-white/50 animate-spin absolute top-1 left-3.5" />
                  ) : (
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${ranking.myStatus === 'active' ? 'translate-x-[22px]' : 'translate-x-0.5'
                      }`} />
                  )}
                </div>
                <span className={`text-sm font-medium ${switchLoading ? 'text-white/40' :
                  ranking.myStatus === 'active' ? 'text-turquoise' : 'text-white/40'
                  }`}>
                  {switchLoading ? '...' : ranking.myStatus === 'active' ? 'Ativo' : 'Inativo'}
                </span>
              </button>
            )}
          </div>
        </motion.div>

        {/* Inactive hint */}
        {statusVerified && ranking.myStatus === 'inactive' && ranking.myPlayerId && ranking.status === 'active' && (
          <Card delay={0.03} className="!p-3">
            <div className="flex items-center gap-2 px-1">
              <AlertTriangle className="w-5 h-5 text-sunny shrink-0" />
              <p className="pl-2 text-sm text-sunny/80">
                Ative sua participação para ser incluído na próxima rodada.
              </p>
            </div>
          </Card>
        )}

        {/* Stats expandable */}
        {SHOW_RANKING_STATS_PANEL && rankingId && (
          <>
            <Card delay={0.05} className="!p-0">
              <button
                onClick={() => {
                  const next = !showStats;
                  setShowStats(next);
                  trackEvent(next ? 'btv_ranking_stats_open' : 'btv_ranking_stats_close');
                  // Prompt on close (not open) so the user has actually seen
                  // the stats at least once before the blocking survey appears.
                  if (!next && !statsFeedbackDone) {
                    setShowStatsSurvey(true);
                    trackEvent('btv_feedback_shown', { feature: STATS_FEEDBACK_FEATURE });
                  }
                }}
                className="w-full flex items-center justify-between px-4 py-3 text-white/70 hover:text-white transition-colors"
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <BarChart3 className="w-4 h-4" />
                  Suas estatísticas
                </span>
                <motion.div
                  animate={{ rotate: showStats ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-4 h-4" />
                </motion.div>
              </button>
            </Card>
            <AnimatePresence initial={false}>
              {showStats && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden space-y-5"
                >
                  <RankingDashboardTab rankingId={rankingId} />
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {/* Tabs */}
        <Card delay={0.1} className="!p-2">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 relative px-2 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.key
                  ? 'bg-turquoise/20 text-turquoise'
                  : 'text-white/50 hover:text-white hover:bg-white/10'
                  }`}
              >
                {tab.label}
                {tab.badge && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-coral text-white text-[10px] flex items-center justify-center">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Card>

        {/* Tab Content */}
        {activeTab === 'leaderboard' && (
          <>
            {/* Metric selector */}
            <Card delay={0.12} className="!p-3">
              <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
                {LEADERBOARD_METRICS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedMetric(option.value)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedMetric === option.value
                      ? 'bg-turquoise/20 text-turquoise'
                      : 'text-white/50 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {partialAvailable && (
                <div className="mt-2 pt-3 border-t border-white/10 px-1">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={partialDiscardsOn}
                    onClick={togglePartialDiscards}
                    className="w-full flex items-center justify-between gap-3 text-left"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm text-white/70">Considerar descartes</span>
                      <span className="block text-[11px] text-white/40 leading-snug">
                        {ranking.discardRounds === 1
                          ? 'Ignora a pior rodada'
                          : `Ignora as ${ranking.discardRounds} piores rodadas`}
                      </span>
                    </span>
                    <span
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${partialDiscardsOn ? 'bg-turquoise' : 'bg-white/15'
                        }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${partialDiscardsOn ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                      />
                    </span>
                  </button>
                </div>
              )}
            </Card>

            {/* Leaderboard */}
            <Card
              delay={0.13}
              className={`!p-0 overflow-hidden transition-opacity ${showPartialStandings && partialLoading ? 'opacity-50' : ''
                }`}
            >
              {displayedLeaderboard.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Trophy className="w-8 h-8 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">
                    Nenhuma partida registrada ainda.
                  </p>
                </div>
              ) : (
                <AnimatePresence mode="popLayout">
                  {displayedLeaderboard.map((player, index) => (
                    <LeaderboardRow
                      key={player.id}
                      player={player}
                      index={index}
                      metric={selectedMetric}
                      hasDiscards={!!ranking.totalRounds && ranking.discardRounds > 0}
                      alwaysShowRounds={showPartialStandings}
                      isCurrentUser={player.subscriberId === subscriber?.id}
                      onShowHistory={setHistoryPlayer}
                    />
                  ))}
                </AnimatePresence>
              )}
            </Card>

            {/* Season evolution (bar-chart race) — mid-season friendly, needs ≥2 completed rounds */}
            {ranking.rounds.filter((r) => r.status === 'completed').length >= 2 && (
              <Card delay={0.15} className="!p-0">
                <button
                  onClick={() => {
                    setShowEvolution(true);
                    trackEvent('btv_ranking_evolution_open');
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 text-white/80 hover:text-white transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <TrendingUp className="w-4 h-4 text-sunny" />
                    Ver evolução da temporada
                  </span>
                  <Play className="w-4 h-4 text-sunny" />
                </button>
              </Card>
            )}
          </>
        )}

        {activeTab === 'rounds' && (
          <RoundsTab
            ranking={ranking}
            isAdmin={isAdmin}
            activePlayerCount={activePlayers.length}
            onDraftRound={handleDraftRound}
            actionLoading={actionLoading}
            onNavigateRound={(roundId) => navigate(`/rankings/${rankingId}/rounds/${roundId}`)}
          />
        )}

        {activeTab === 'players' && (
          <PlayersTab
            isAdmin={isAdmin}
            isCreator={isCreator}
            rankingId={ranking.id}
            rankingName={ranking.name}
            activePlayers={activePlayers}
            inactivePlayers={inactivePlayers}
            pendingPlayers={pendingPlayers}
            pendingPaymentPlayers={pendingPaymentPlayers}
            paymentEnabled={!!ranking.paymentInfo?.enabled}
            onPlayerAction={handlePlayerAction}
            actionLoading={actionLoading}
            currentSubscriberId={subscriber?.id || ''}
          />
        )}

        {activeTab === 'settings' && isAdmin && (
          <SettingsTab
            ranking={ranking}
            isCreator={isCreator}
            onFinish={() => setShowFinishConfirm(true)}
            onReactivate={handleReactivate}
            onDelete={() => setShowDeleteConfirm(true)}
            onEditScoring={() => setShowScoringEdit(true)}
            onEditActivation={() => setShowActivationEdit(true)}
            onNewSeason={() => setShowNewSeason(true)}
            onShareInvite={handleShareInvite}
            onBonusChange={loadRanking}
            onOverlayUpload={async (base64) => {
              setActionLoading('overlay');
              try {
                const res = await api.rankings.uploadOverlay(rankingId!, base64);
                if (res.success) {
                  await loadRanking();
                } else {
                  alert(res.error?.message || 'Erro ao enviar overlay');
                }
              } finally {
                setActionLoading(null);
              }
            }}
            onOverlayDelete={async () => {
              setActionLoading('overlay');
              try {
                await api.rankings.deleteOverlay(rankingId!);
                await loadRanking();
              } finally {
                setActionLoading(null);
              }
            }}
            copiedInvite={copiedInvite}
            actionLoading={actionLoading}
            leaderboard={leaderboard}
            selectedMetric={selectedMetric}
          />
        )}

        {activeTab === 'info' && !isAdmin && (
          <InfoTab ranking={ranking} />
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {historyPlayer && rankingId && (
          <PlayerHistoryModal
            rankingId={rankingId}
            player={historyPlayer}
            partialDiscards={showPartialStandings}
            onClose={() => setHistoryPlayer(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEvolution && rankingId && (
          <RankingEvolutionView
            rankingId={rankingId}
            rankingName={ranking.name}
            rankingImageUrl={ranking.imageUrl}
            onClose={() => setShowEvolution(false)}
          />
        )}
      </AnimatePresence>
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title={ranking.seasons.length > 1 ? 'Excluir temporada' : 'Excluir ranking'}
        message={
          ranking.seasons.length > 1
            ? `Tem certeza que deseja excluir a temporada "${ranking.subtitle}"? Esta ação não pode ser desfeita.`
            : `Tem certeza que deseja excluir "${ranking.name}"? Esta ação não pode ser desfeita.`
        }
        confirmLabel="Excluir"
      />

      <ConfirmModal
        isOpen={showFinishConfirm}
        onClose={() => setShowFinishConfirm(false)}
        onConfirm={handleFinish}
        isLoading={actionLoading === 'finish'}
        title="Encerrar ranking"
        message="Tem certeza que deseja encerrar este ranking? Nenhuma nova rodada poderá ser criada."
        confirmLabel="Encerrar"
      />

      <ScoringEditModal
        isOpen={showScoringEdit}
        onClose={() => setShowScoringEdit(false)}
        ranking={ranking}
        onSaved={loadRanking}
      />

      <ActivationEditModal
        isOpen={showActivationEdit}
        onClose={() => setShowActivationEdit(false)}
        ranking={ranking}
        onSaved={loadRanking}
      />

      <NewSeasonModal
        isOpen={showNewSeason}
        onClose={() => setShowNewSeason(false)}
        rankingId={ranking.id}
      />

      {/* Stats panel feedback survey (blocking, one-time) */}
      <AnimatePresence>
        {showStatsSurvey && (
          <FeedbackSurveyModal
            feature={STATS_FEEDBACK_FEATURE}
            title="Essas estatísticas são úteis pra você?"
            subtitle="Sua resposta nos ajuda a melhorar o BT Vision."
            ratingOptions={STATS_FEEDBACK_RATINGS}
            commentLabel="O que você mudaria ou queria ver aqui?"
            onSubmitted={() => {
              writeLS(STATS_FEEDBACK_LS_KEY, true);
              setStatsFeedbackDone(true);
              setShowStatsSurvey(false);
            }}
          />
        )}
      </AnimatePresence>
    </AppShell>
  );
}

// ============================================================================
// Leaderboard Row
// ============================================================================

interface LeaderboardRowProps {
  player: RankingPlayer;
  index: number;
  metric: RankingLeaderboardMetric;
  /** Season declares discard rounds, so totals may exclude a player's worst rounds. */
  hasDiscards: boolean;
  /**
   * "Considerar descartes" is on: show the rounds line on every row, even with
   * nothing discarded, so "2 rodadas · 0 descartadas" explains a short total.
   */
  alwaysShowRounds: boolean;
  isCurrentUser: boolean;
  onShowHistory: (player: RankingPlayer) => void;
}

/**
 * forwardRef because this renders directly inside <AnimatePresence
 * mode="popLayout">, which wraps each child and measures it through a ref to
 * hold an exiting row in place while the rest reflow. A plain function
 * component silently drops that ref — React warns, and the pop animation
 * measures nothing.
 */
const LeaderboardRow = forwardRef<HTMLDivElement, LeaderboardRowProps>(function LeaderboardRow(
  { player, index, metric, hasDiscards, alwaysShowRounds, isCurrentUser, onShowHistory },
  ref
) {
  const position = leaderboardPosition(player, index, metric);

  const getValue = () => {
    if (metric === 'win_rate') {
      const rate = player.matchesPlayed > 0 ? (player.matchesWon / player.matchesPlayed) * 100 : 0;
      return `${rate.toFixed(1)}%`;
    }
    if (metric === 'game_balance') {
      const balance = player.gamesWon - player.gamesLost;
      return balance > 0 ? `+${balance}` : balance;
    }
    const map: Record<string, number> = {
      total_points: player.totalPoints,
      matches_won: player.matchesWon,
      matches_lost: player.matchesLost,
      matches_played: player.matchesPlayed,
      games_won: player.gamesWon,
      rounds_played: player.roundsPlayed,
    };
    return map[metric] ?? 0;
  };

  const positionDisplay = () => {
    if (metric === 'total_points') {
      if (position === 1) return <Medal className="w-5 h-5 text-sunny" />;
      if (position === 2) return <Medal className="w-5 h-5 text-white/60" />;
      if (position === 3) return <Medal className="w-5 h-5 text-[#CD7F32]" />;
      if (position === 4 || position === 5) return <Medal className="w-5 h-5 text-turquoise" />;
    }
    return <span className="text-sm text-white/40 font-medium w-5 text-center">{position}</span>;
  };

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, delay: index * 0.03 }}
      className={`flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-b-0 ${isCurrentUser ? 'bg-turquoise/5' : ''
        }`}
    >
      <div className="w-6 flex justify-center shrink-0">{positionDisplay()}</div>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-turquoise/30 to-coral/30 flex items-center justify-center text-white font-bold text-sm shrink-0">
        {player.avatarUrl ? (
          <img src={player.avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          player.name?.charAt(0) || '?'
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${isCurrentUser ? 'font-semibold text-turquoise' : 'text-white'}`}>
          {player.name || 'Jogador'}
          {isCurrentUser && <span className="text-xs text-turquoise/60 ml-1">(você)</span>}
        </p>
        {/* Discards shape every metric's counters, not just Pontos. */}
        {hasDiscards && (alwaysShowRounds || player.discardedRounds > 0) && (
          <p className="text-[11px] text-white/30">
            {player.roundsPlayed} rodada{player.roundsPlayed === 1 ? '' : 's'} · {player.discardedRounds}{' '}
            descartada{player.discardedRounds === 1 ? '' : 's'}
          </p>
        )}
      </div>
      <div className="text-right shrink-0">
        <AnimatePresence mode="wait">
          <motion.p
            key={metric}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className={`font-bold tabular-nums ${isCurrentUser ? 'text-turquoise' : 'text-white'}`}
          >
            {getValue()}
          </motion.p>
        </AnimatePresence>
      </div>
      <button
        onClick={() => onShowHistory(player)}
        className="p-1.5 text-white/20 hover:text-turquoise hover:bg-white/10 rounded-lg transition-colors shrink-0"
        title="Histórico"
      >
        <History className="w-4 h-4" />
      </button>
    </motion.div>
  );
});

// ============================================================================
// Stats Tab (Ranking Dashboard)
// ============================================================================

type OpponentSort = 'matches' | 'bestWinRate' | 'worstWinRate';

function RankingDashboardTab({ rankingId }: { rankingId: string }) {
  const [data, setData] = useState<RankingDashboardData | null>(rankingDashboardCache.get(rankingId) || null);
  const [isLoading, setIsLoading] = useState(!rankingDashboardCache.has(rankingId));
  const [opponentSort, setOpponentSort] = useState<OpponentSort>('matches');
  const [showSortMenu, setShowSortMenu] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.rankings.dashboard(rankingId);
        if (!cancelled && res.success && res.data) {
          setData(res.data);
          rankingDashboardCache.set(rankingId, res.data);
        }
      } catch {
        // keep cached data if available
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [rankingId]);

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-turquoise animate-spin" />
        </div>
      </Card>
    );
  }

  if (!data || (data.partners.length === 0 && data.opponents.length === 0 && data.roundWinRates.length === 0)) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-white/50 text-sm">
            Jogue algumas partidas para ver suas estatísticas!
          </p>
        </div>
      </Card>
    );
  }

  // Sorted opponents
  const sortedOpponents = [...data.opponents].sort((a, b) => {
    switch (opponentSort) {
      case 'bestWinRate':
        return b.winRate - a.winRate || b.matches - a.matches;
      case 'worstWinRate':
        return a.winRate - b.winRate || b.matches - a.matches;
      case 'matches':
      default:
        return b.matches - a.matches || b.winRate - a.winRate;
    }
  });
  const top5Opponents = sortedOpponents.slice(0, 5);

  return (
    <>
      {/* Win Rate per Round */}
      {data.roundWinRates.length > 0 && (
        <Card>
          <h3 className="font-semibold text-white flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-turquoise" />
            Aproveitamento por Rodada
          </h3>
          <div className="h-48 flex items-end justify-between gap-2 px-2">
            {data.roundWinRates.map((point, index) => (
              <motion.div
                key={point.roundNumber}
                initial={{ height: 0 }}
                animate={{ height: `${Math.max(point.winRate, 5)}%` }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.05 }}
                className="flex-1 bg-gradient-to-t from-turquoise/50 to-turquoise rounded-t-md relative group min-h-[24px]"
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 + index * 0.05 }}
                  className="absolute -top-5 left-1/2 -translate-x-1/2 text-[11px] font-medium text-white whitespace-nowrap"
                >
                  {point.winRate}%
                </motion.div>
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-white/50">
                  R{point.roundNumber}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="h-6" />
        </Card>
      )}

      {/* Partners (Top 5) */}
      {data.partners.length > 0 && (
        <Card>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-coral/20 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-coral" />
            </div>
            <div>
              <p className="text-white font-medium">Melhores parcerias</p>
              <p className="text-white/50 text-sm">Aproveitamento por dupla</p>
            </div>
          </div>
          <div className="space-y-3">
            {data.partners.map((partner, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-white/80 text-sm truncate max-w-[120px]">{partner.name}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${partner.winRate >= 50 ? 'bg-success-green' : 'bg-warning-orange'
                        }`}
                      style={{ width: `${partner.winRate}%` }}
                    />
                  </div>
                  <span className="text-white text-sm font-medium w-12 text-right">
                    {partner.winRate}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Opponents */}
      {data.opponents.length > 0 && (
        <Card>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-turquoise/20 flex items-center justify-center shrink-0">
                <Swords className="w-5 h-5 text-turquoise" />
              </div>
              <div>
                <p className="text-white font-medium">Seus adversários</p>
                <p className="text-white/50 text-sm">Histórico contra oponentes</p>
              </div>
            </div>
            <div className="relative">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className={`p-1.5 rounded-lg transition-colors ${showSortMenu ? 'bg-turquoise/20 text-turquoise' : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70'
                  }`}
              >
                <ArrowUpDown className="w-4 h-4" />
              </button>
              {showSortMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowSortMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-[#1a1a2e] border border-white/15 rounded-lg shadow-xl overflow-hidden min-w-[140px]">
                    {([
                      { id: 'matches' as OpponentSort, label: '+ Jogos' },
                      { id: 'bestWinRate' as OpponentSort, label: '+ Vitórias' },
                      { id: 'worstWinRate' as OpponentSort, label: '+ Derrotas' },
                    ]).map((option) => (
                      <button
                        key={option.id}
                        onClick={() => { setOpponentSort(option.id); setShowSortMenu(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${opponentSort === option.id
                          ? 'bg-turquoise/15 text-turquoise font-medium'
                          : 'text-white/70 hover:bg-white/10'
                          }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <PlayerStatHeader label="Adversário" />
            <AnimatePresence mode="popLayout">
              {top5Opponents.map((opponent, index) => (
                <motion.div
                  key={opponent.name}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25, delay: index * 0.03 }}
                >
                  <PlayerStatRow {...opponent} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </Card>
      )}
    </>
  );
}

// ============================================================================
// Rounds Tab
// ============================================================================

function RoundsTab({
  ranking,
  isAdmin,
  activePlayerCount,
  onDraftRound,
  actionLoading,
  onNavigateRound,
}: {
  ranking: RankingDetail;
  isAdmin: boolean;
  activePlayerCount: number;
  onDraftRound: () => void;
  actionLoading: string | null;
  onNavigateRound: (roundId: string) => void;
}) {
  const sortedRounds = useMemo(() =>
    [...ranking.rounds].sort((a, b) => b.roundNumber - a.roundNumber),
    [ranking.rounds]
  );

  const hasIncompleteRound = ranking.rounds.some(r => r.status !== 'completed');
  const notEnoughPlayers = activePlayerCount < 4;
  const draftDisabled = hasIncompleteRound || notEnoughPlayers;

  return (
    <>
      {isAdmin && ranking.status === 'active' && (
        <Card delay={0.12}>
          <Button
            onClick={onDraftRound}
            fullWidth
            isLoading={actionLoading === 'draft'}
            disabled={draftDisabled}
          >
            <Play className="w-4 h-4" />
            Sortear nova rodada
          </Button>
          {notEnoughPlayers && (
            <p className="text-xs text-sunny/80 mt-2 text-center">
              É necessário ter pelo menos 4 jogadores aprovados e ativos para sortear uma rodada.
            </p>
          )}
          {hasIncompleteRound && !notEnoughPlayers && (
            <p className="text-xs text-sunny/80 mt-2 text-center">
              Todas as partidas da rodada atual devem ser concluídas antes de sortear uma nova. Atribua W.O. para partidas não realizadas.
            </p>
          )}
          {ranking.autoDraft?.enabled && ranking.autoDraft.nextAutoDraftAt && (
            <p className="text-xs text-white/40 mt-2 text-center">
              Sorteio automático {formatScheduleMoment(ranking.autoDraft.nextAutoDraftAt, ranking.autoDraft.timezone)}{' '}
              se ninguém sortear antes.
            </p>
          )}
        </Card>
      )}

      {sortedRounds.length === 0 ? (
        <Card delay={0.15}>
          <div className="text-center py-6">
            <p className="text-white/40 text-sm">Nenhuma rodada criada ainda.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {sortedRounds.map((round, index) => (
            <motion.div
              key={round.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 + index * 0.04 }}
            >
              <button
                onClick={() => onNavigateRound(round.id)}
                className="w-full glass-card rounded-2xl p-4 text-left hover:bg-white/[0.08] transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-white">
                    Rodada {round.roundNumber}
                    {ranking.totalRounds ? (
                      <span className="text-white/40 font-normal"> de {ranking.totalRounds}</span>
                    ) : null}
                  </h3>
                  <RoundStatusBadge status={round.status} />
                </div>
                <div className="flex items-center gap-4 text-xs text-white/50">
                  <span>{round.completedMatches}/{round.totalMatches} partidas</span>
                  {round.pendingMatches > 0 && (
                    <span className="text-sunny">{round.pendingMatches} pendente{round.pendingMatches > 1 ? 's' : ''}</span>
                  )}
                  {round.disputedMatches > 0 && (
                    <span className="text-coral flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {round.disputedMatches} disputada{round.disputedMatches > 1 ? 's' : ''}
                    </span>
                  )}
                  {round.lockedMatches > 0 && (
                    <span className="text-white/30">{round.lockedMatches} travada{round.lockedMatches > 1 ? 's' : ''}</span>
                  )}
                </div>
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </>
  );
}

function RoundStatusBadge({ status }: { status: RankingRoundSummary['status'] }) {
  const config = {
    drafted: { label: 'Rascunho', className: 'bg-white/10 text-white/50' },
    in_progress: { label: 'Em andamento', className: 'bg-turquoise/20 text-turquoise' },
    completed: { label: 'Concluída', className: 'bg-green-500/20 text-green-400' },
  };
  const { label, className } = config[status];
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${className}`}>
      {label}
    </span>
  );
}

// ============================================================================
// Players Tab
// ============================================================================

function PaymentBadge({ player }: { player: RankingPlayer }) {
  if (player.invoiceStatus === 'paid') {
    return (
      <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-400/10 text-green-400">
        {player.paidVia === 'cash' ? 'Pago (dinheiro)' : 'Pago'}
      </span>
    );
  }
  if (player.invoiceStatus === 'cancelled') {
    return (
      <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/10 text-white/50">
        Isento
      </span>
    );
  }
  return null;
}

/**
 * The nudge an organizer pastes into the ranking's WhatsApp group: who is still
 * inactive plus the deep link they activate themselves on. `*` is WhatsApp bold.
 */
function buildActivationReminder(
  rankingId: string,
  rankingName: string,
  inactivePlayers: RankingPlayer[]
): string {
  const names = inactivePlayers.map((p) => `• ${p.name || 'Jogador'}`).join('\n');
  const url = `${canonicalOrigin()}/rankings/${rankingId}`;
  return (
    `🏆 *Ranking ${rankingName}*\n\n` +
    `${inactivePlayers.length} ainda não confirmaram presença para a próxima rodada:\n${names}\n\n` +
    `Se você vai jogar, ative sua presença aqui 👇\n${url}`
  );
}

function PlayersTab({
  isAdmin,
  isCreator,
  rankingId,
  rankingName,
  activePlayers,
  inactivePlayers,
  pendingPlayers,
  pendingPaymentPlayers,
  paymentEnabled,
  onPlayerAction,
  actionLoading,
  currentSubscriberId,
}: {
  isAdmin: boolean;
  isCreator: boolean;
  rankingId: string;
  rankingName: string;
  activePlayers: RankingPlayer[];
  inactivePlayers: RankingPlayer[];
  pendingPlayers: RankingPlayer[];
  pendingPaymentPlayers: RankingPlayer[];
  paymentEnabled: boolean;
  onPlayerAction: (playerId: string, action: string) => void;
  actionLoading: string | null;
  currentSubscriberId: string;
}) {
  const [exemptTarget, setExemptTarget] = useState<RankingPlayer | null>(null);
  const [markPaidTarget, setMarkPaidTarget] = useState<RankingPlayer | null>(null);
  const [rejectPaymentTarget, setRejectPaymentTarget] = useState<RankingPlayer | null>(null);
  const [removeTarget, setRemoveTarget] = useState<RankingPlayer | null>(null);

  const activationReminder = useMemo(
    () => buildActivationReminder(rankingId, rankingName, inactivePlayers),
    [rankingId, rankingName, inactivePlayers]
  );

  return (
    <>
      {/* Awaiting payment (paid rankings) */}
      {pendingPaymentPlayers.length > 0 && (
        <Card delay={0.1}>
          <h3 className="text-sm font-semibold text-sunny mb-3 flex items-center gap-2">
            <QrCode className="w-4 h-4" />
            Aguardando pagamento ({pendingPaymentPlayers.length})
          </h3>
          <div className="space-y-2">
            {pendingPaymentPlayers.map((player) => (
              <div key={player.id} className="flex items-center gap-3 p-2 rounded-xl bg-white/5">
                <PlayerAvatar player={player} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{player.name || 'Jogador'}</p>
                  <p className="text-[11px] flex items-center gap-1.5">
                    {player.invoiceStatus === 'overdue' ? (
                      <span className="text-coral font-medium">Vencido</span>
                    ) : player.invoiceStatus === 'pending_creation' ? (
                      <span className="text-white/40">Gerando cobrança…</span>
                    ) : (
                      <span className="text-sunny">Pendente</span>
                    )}
                    {player.invoiceDueDate && (
                      <span className="text-white/40">
                        vence {formatBonusDate(player.invoiceDueDate)}
                      </span>
                    )}
                  </p>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => setMarkPaidTarget(player)}
                      disabled={actionLoading !== null}
                      className="p-1.5 text-green-400 hover:bg-green-400/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Marcar como pago (dinheiro)"
                    >
                      {actionLoading === `mark-paid-${player.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setExemptTarget(player)}
                      disabled={actionLoading !== null}
                      className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Isentar da taxa"
                    >
                      {actionLoading === `exempt-${player.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setRejectPaymentTarget(player)}
                      disabled={actionLoading !== null}
                      className="p-1.5 text-coral hover:bg-coral/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Recusar (cancela a cobrança)"
                    >
                      {actionLoading === `reject-${player.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <ConfirmModal
        isOpen={!!markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        onConfirm={() => {
          if (markPaidTarget) onPlayerAction(markPaidTarget.id, 'mark-paid');
          setMarkPaidTarget(null);
        }}
        isLoading={false}
        title="Marcar como pago"
        message={`Confirmar que ${markPaidTarget?.name || 'este jogador'} pagou a inscrição em dinheiro? A cobrança PIX será baixada.`}
        confirmLabel="Confirmar pagamento"
      />

      <ConfirmModal
        isOpen={!!exemptTarget}
        onClose={() => setExemptTarget(null)}
        onConfirm={() => {
          if (exemptTarget) onPlayerAction(exemptTarget.id, 'exempt');
          setExemptTarget(null);
        }}
        isLoading={false}
        title="Isentar da taxa"
        message={`Isentar ${exemptTarget?.name || 'este jogador'} da taxa de inscrição? A cobrança PIX será cancelada e o jogador entra sem pagar.`}
        confirmLabel="Isentar"
      />

      <ConfirmModal
        isOpen={!!rejectPaymentTarget}
        onClose={() => setRejectPaymentTarget(null)}
        onConfirm={() => {
          if (rejectPaymentTarget) onPlayerAction(rejectPaymentTarget.id, 'reject');
          setRejectPaymentTarget(null);
        }}
        isLoading={false}
        title="Recusar jogador"
        message={`Recusar a entrada de ${rejectPaymentTarget?.name || 'este jogador'}? A cobrança PIX será cancelada e o jogador ficará fora do ranking.`}
        confirmLabel="Recusar"
      />

      <ConfirmModal
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (removeTarget) onPlayerAction(removeTarget.id, 'remove');
          setRemoveTarget(null);
        }}
        isLoading={false}
        title="Remover jogador"
        message={`Remover ${removeTarget?.name || 'este jogador'} do ranking?${paymentEnabled ? ' Qualquer cobrança PIX em aberto será cancelada.' : ''}`}
        confirmLabel="Remover"
      />

      {/* Pending requests */}
      {pendingPlayers.length > 0 && isAdmin && (
        <Card delay={0.12}>
          <h3 className="text-sm font-semibold text-sunny mb-3 flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Pedidos pendentes ({pendingPlayers.length})
          </h3>
          <div className="space-y-2">
            {pendingPlayers.map((player) => (
              <div key={player.id} className="flex items-center gap-3 p-2 rounded-xl bg-white/5">
                <PlayerAvatar player={player} size="sm" />
                <p className="flex-1 text-sm text-white truncate">{player.name || 'Jogador'}</p>
                <div className="flex gap-1">
                  <button
                    onClick={() => onPlayerAction(player.id, 'approve')}
                    disabled={actionLoading !== null}
                    className="p-1.5 text-green-400 hover:bg-green-400/10 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {actionLoading === `approve-${player.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => onPlayerAction(player.id, 'reject')}
                    disabled={actionLoading !== null}
                    className="p-1.5 text-coral hover:bg-coral/10 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {actionLoading === `reject-${player.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Active players */}
      <Card delay={0.15}>
        <h3 className="text-sm font-semibold text-white/70 mb-3 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Ativos ({activePlayers.length})
        </h3>
        <div className="space-y-1">
          {activePlayers.map((player) => (
            <div key={player.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors group">
              <PlayerAvatar player={player} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className={`text-sm truncate ${player.subscriberId === currentSubscriberId ? 'text-turquoise font-semibold' : 'text-white'
                    }`}>
                    {player.name || 'Jogador'}
                  </p>
                  {player.role === 'admin' && (
                    <Crown className="w-3 h-3 text-sunny shrink-0" />
                  )}
                  {isAdmin && paymentEnabled && <PaymentBadge player={player} />}
                </div>
                <p className="text-[11px] text-white/40">{player.totalPoints} pts</p>
              </div>

              {/* Self-deactivate button */}
              {player.subscriberId === currentSubscriberId && !isAdmin && (
                <div className="flex gap-1">
                  <button
                    onClick={() => onPlayerAction(player.id, 'toggle')}
                    disabled={actionLoading !== null}
                    className="p-1.5 text-white/30 hover:text-sunny hover:bg-sunny/10 rounded-lg transition-colors disabled:opacity-50"
                    title="Desativar"
                  >
                    {actionLoading === `toggle-${player.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                  </button>
                </div>
              )}

              {isAdmin && player.subscriberId !== currentSubscriberId && (
                <div className="flex gap-1">
                  <button
                    onClick={() => onPlayerAction(player.id, 'toggle')}
                    disabled={actionLoading !== null}
                    className="p-1.5 text-white/30 hover:text-sunny hover:bg-sunny/10 rounded-lg transition-colors disabled:opacity-50"
                    title="Desativar"
                  >
                    {actionLoading === `toggle-${player.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                  </button>
                  {isCreator && player.role === 'player' && (
                    <button
                      onClick={() => onPlayerAction(player.id, 'promote')}
                      disabled={actionLoading !== null}
                      className="p-1.5 text-white/30 hover:text-turquoise hover:bg-turquoise/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Promover a admin"
                    >
                      {actionLoading === `promote-${player.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  {isCreator && player.role === 'admin' && (
                    <button
                      onClick={() => onPlayerAction(player.id, 'demote')}
                      disabled={actionLoading !== null}
                      className="p-1.5 text-white/30 hover:text-coral hover:bg-coral/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Remover admin"
                    >
                      {actionLoading === `demote-${player.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldOff className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  <button
                    onClick={() => setRemoveTarget(player)}
                    disabled={actionLoading !== null}
                    className="p-1.5 text-white/30 hover:text-coral hover:bg-coral/10 rounded-lg transition-colors disabled:opacity-50"
                    title="Remover"
                  >
                    {actionLoading === `remove-${player.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Inactive players */}
      {inactivePlayers.length > 0 && (
        <Card delay={0.18}>
          <h3 className="text-sm font-semibold text-white/40 mb-3">
            Inativos ({inactivePlayers.length})
          </h3>
          <div className="space-y-1 opacity-60">
            {inactivePlayers.map((player) => (
              <div key={player.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors group">
                <PlayerAvatar player={player} size="sm" />
                <p className="flex-1 text-sm text-white/50 truncate">{player.name || 'Jogador'}</p>
                {isAdmin && paymentEnabled && <PaymentBadge player={player} />}
                {(isAdmin || player.subscriberId === currentSubscriberId) && (
                  <button
                    onClick={() => onPlayerAction(player.id, 'toggle')}
                    disabled={actionLoading !== null}
                    className="p-1.5 text-white/30 hover:text-turquoise transition-all disabled:opacity-50"
                    title="Reativar"
                  >
                    {actionLoading === `toggle-${player.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Nudge the stragglers in the ranking's WhatsApp group (admins only) */}
          {isAdmin && (
            <WhatsAppMessageActions
              message={activationReminder}
              label="Cobrar ativação no WhatsApp"
              onShare={() =>
                trackEvent('btv_ranking_activation_reminder_share', {
                  inactive_count: inactivePlayers.length,
                })
              }
              className="mt-3 pt-3 border-t border-white/10"
            />
          )}
        </Card>
      )}
    </>
  );
}

// ============================================================================
// Info Tab (read-only for non-admin users)
// ============================================================================

function InfoTab({ ranking }: { ranking: RankingDetail }) {
  return (
    <>
      {ranking.description && (
        <Card delay={0.12}>
          <h3 className="text-sm font-semibold text-white/70 mb-2">Descrição</h3>
          <p className="text-sm text-white/50 whitespace-pre-line">{ranking.description}</p>
        </Card>
      )}
      <Card delay={0.14}>
        <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2 mb-3">
          <Info className="w-4 h-4" />
          Pontuação
        </h3>
        <ScoringRulesSummary rules={ranking.scoringRules} />
      </Card>
      {ranking.totalRounds ? (
        <Card delay={0.16}>
          <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2 mb-3">
            <Info className="w-4 h-4" />
            Formato da temporada
          </h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <FormatDisplay label="Total de rodadas" value={ranking.totalRounds} />
            <FormatDisplay label="Descartes" value={ranking.discardRounds} />
          </div>
          {ranking.discardRounds > 0 && (
            <p className="text-xs text-white/40 mt-3 leading-snug">
              Cada jogador soma apenas suas {ranking.totalRounds - ranking.discardRounds} melhores
              rodadas. Rodadas não jogadas contam como descarte.
            </p>
          )}
          <p className="text-xs text-white/40 mt-3 leading-snug">
            Desempate: vitórias, saldo de games e confronto direto entre os empatados. Só contam
            as partidas que pontuaram — coringa sem pontos e rodadas descartadas ficam de fora.
          </p>
        </Card>
      ) : null}
    </>
  );
}

// ============================================================================
// Settings Tab
// ============================================================================

function SettingsTab({
  ranking,
  isCreator,
  onFinish,
  onReactivate,
  onDelete,
  onEditScoring,
  onEditActivation,
  onNewSeason,
  onShareInvite,
  onBonusChange,
  //onOverlayUpload,
  //onOverlayDelete,
  copiedInvite,
  actionLoading,
  leaderboard,
  selectedMetric,
}: {
  ranking: RankingDetail;
  isCreator: boolean;
  onFinish: () => void;
  onReactivate: () => void;
  onDelete: () => void;
  onEditScoring: () => void;
  onEditActivation: () => void;
  onNewSeason: () => void;
  onShareInvite: () => void;
  onBonusChange: () => void | Promise<void>;
  onOverlayUpload: (base64: string) => Promise<void>;
  onOverlayDelete: () => Promise<void>;
  copiedInvite: boolean;
  actionLoading: string | null;
  leaderboard: RankingPlayer[];
  selectedMetric: RankingLeaderboardMetric;
}) {
  const [showPaymentEdit, setShowPaymentEdit] = useState(false);
  const [showAutoDraftEdit, setShowAutoDraftEdit] = useState(false);

  /**
   * The latest round that actually fed the standings. A freshly drafted round
   * has no submitted scores yet, so labelling the share card with it would
   * claim more than the leaderboard shows.
   */
  const lastPlayedRoundNumber = useMemo(() => {
    const played = ranking.rounds.filter(r => r.status !== 'drafted');
    return played.length > 0 ? Math.max(...played.map(r => r.roundNumber)) : null;
  }, [ranking.rounds]);

  const paymentCounts = useMemo(() => {
    const counts = { paid: 0, pending: 0, overdue: 0 };
    for (const p of ranking.players) {
      if (p.invoiceStatus === 'paid') counts.paid++;
      else if (p.invoiceStatus === 'overdue') counts.overdue++;
      else if (p.invoiceStatus === 'pending' || p.invoiceStatus === 'pending_creation') counts.pending++;
    }
    return counts;
  }, [ranking.players]);

  return (
    <>
      {/* Invite Code */}
      <Card delay={0.12}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Código de convite</p>
            <p className="font-mono text-lg font-bold text-turquoise tracking-widest">
              {ranking.inviteCode}
            </p>
          </div>
          <Button onClick={onShareInvite} size="sm" variant="secondary">
            {copiedInvite ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copiedInvite ? 'Copiado!' : 'Compartilhar'}
          </Button>
        </div>
      </Card>

      {/* Share buttons */}
      {leaderboard.length >= 5 && (
        <Card delay={0.13}>
          <RankingShareButton
            rankingName={ranking.name}
            roundNumber={lastPlayedRoundNumber}
            imageUrl={ranking.imageUrl}
            leaderboard={leaderboard}
            metric={selectedMetric}
          />
        </Card>
      )}

      {/* Overlay upload */}
      {/*
      <Card delay={0.135}>
        <OverlayUpload
          overlayUrl={ranking.overlayUrl}
          onUpload={onOverlayUpload}
          onDelete={onOverlayDelete}
          isLoading={actionLoading === 'overlay'}
        />
      </Card>
      */}

      {ranking.description && (
        <Card delay={0.14}>
          <h3 className="text-sm font-semibold text-white/70 mb-2">Descrição</h3>
          <p className="text-sm text-white/50 whitespace-pre-line">{ranking.description}</p>
        </Card>
      )}

      {/* Scoring rules */}
      <Card delay={0.16}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Pontuação
          </h3>
          {ranking.status === 'active' && (
            <button onClick={onEditScoring} className="text-xs text-turquoise hover:text-turquoise/80 transition-colors">
              Editar
            </button>
          )}
        </div>
        <ScoringRulesSummary rules={ranking.scoringRules} />
        <div className="grid grid-cols-2 gap-2 text-sm mt-2">
          <FormatDisplay label="Total de rodadas" value={ranking.totalRounds ?? '—'} />
          <FormatDisplay label="Descartes" value={ranking.totalRounds ? ranking.discardRounds : '—'} />
        </div>
      </Card>

      {/* Entry fee (Asaas) */}
      <Card delay={0.165}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2">
            <QrCode className="w-4 h-4" />
            Inscrição paga
          </h3>
          {ranking.paymentInfo?.enabled && ranking.status === 'active' && (
            <button onClick={() => setShowPaymentEdit(true)} className="text-xs text-turquoise hover:text-turquoise/80 transition-colors">
              Editar
            </button>
          )}
        </div>
        {ranking.paymentInfo?.enabled ? (
          <>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <FormatDisplay label="Valor" value={formatBRLCents(ranking.paymentInfo.amountCents)} />
              <FormatDisplay label="Prazo" value={`${ranking.paymentInfo.dueDays ?? '—'} dia(s)`} />
            </div>
            <div className="flex gap-3 mt-3 text-xs">
              <span className="text-green-400">{paymentCounts.paid} pago(s)</span>
              <span className="text-sunny">{paymentCounts.pending} pendente(s)</span>
              {paymentCounts.overdue > 0 && (
                <span className="text-coral">{paymentCounts.overdue} vencido(s)</span>
              )}
            </div>
            <p className="text-xs text-white/40 mt-3 leading-snug">
              Alterações valem apenas para cobranças futuras. As cobranças vão para a
              conta Asaas do criador do ranking.
              {ranking.paymentInfo.platformFeePercent
                ? ` O BT Vision retém ${ranking.paymentInfo.platformFeePercent}% de taxa de serviço sobre cada inscrição paga.`
                : ''}
            </p>
          </>
        ) : (
          <p className="text-sm text-white/50">
            Este ranking não cobra inscrição. Para cobrar inscrição, crie um novo ranking.
          </p>
        )}
      </Card>

      <PaymentEditModal
        isOpen={showPaymentEdit}
        onClose={() => setShowPaymentEdit(false)}
        ranking={ranking}
        onSaved={onBonusChange}
      />

      {/* Bonus points */}
      <BonusesCard ranking={ranking} onChange={onBonusChange} />

      {/* Activation window */}
      <Card delay={0.17}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2">
            <CalendarClock className="w-4 h-4" />
            Inscrições
          </h3>
          {ranking.status === 'active' && (
            <button onClick={onEditActivation} className="text-xs text-turquoise hover:text-turquoise/80 transition-colors">
              Editar
            </button>
          )}
        </div>
        {ranking.activation.mode === 'scheduled' ? (
          <>
            <p className="text-sm text-white/70">
              Reabrem <span className="text-turquoise font-semibold">{ranking.activation.description}</span>
            </p>
            <p className="text-xs text-white/40 mt-2 leading-snug">
              Todos os jogadores voltam para inativo nesse horário e recebem um email para
              confirmar a próxima rodada — mesmo que a rodada atual ainda esteja rolando.
            </p>
            {ranking.activation.nextActivationAt && (
              <p className="text-xs text-white/40 mt-2">
                Próxima: {formatScheduleMoment(ranking.activation.nextActivationAt, ranking.activation.timezone)}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-white/70">Ao encerrar cada rodada</p>
            <p className="text-xs text-white/40 mt-2 leading-snug">
              Os jogadores voltam para inativo quando a rodada termina e precisam se reativar
              antes do sorteio da próxima.
            </p>
          </>
        )}
      </Card>

      {/* Weekly auto-draft fallback */}
      <AutoDraftCard
        ranking={ranking}
        onEdit={() => setShowAutoDraftEdit(true)}
        onChange={onBonusChange}
      />
      <AutoDraftEditModal
        isOpen={showAutoDraftEdit}
        onClose={() => setShowAutoDraftEdit(false)}
        ranking={ranking}
        onSaved={onBonusChange}
      />

      {/* Actions */}
      <Card delay={0.16}>
        <div className="space-y-2">
          {ranking.status === 'active' ? (
            <Button
              onClick={onFinish}
              variant="ghost"
              fullWidth
              className="text-sunny hover:bg-sunny/10"
              isLoading={actionLoading === 'finish'}
            >
              Encerrar ranking
            </Button>
          ) : (
            <>
              <Button
                onClick={onReactivate}
                variant="ghost"
                fullWidth
                className="text-turquoise hover:bg-turquoise/10"
                isLoading={actionLoading === 'reactivate'}
              >
                Reativar ranking
              </Button>
              <Button
                onClick={onNewSeason}
                variant="ghost"
                fullWidth
                className="text-turquoise hover:bg-turquoise/10"
              >
                Nova temporada
              </Button>
            </>
          )}
          {isCreator && (
            <Button
              onClick={onDelete}
              variant="ghost"
              fullWidth
              className="text-coral hover:bg-coral/10"
            >
              <Trash2 className="w-4 h-4" />
              {ranking.seasons.length > 1 ? 'Excluir temporada' : 'Excluir ranking'}
            </Button>
          )}
        </div>
      </Card>
    </>
  );
}

/*
function OverlayUpload({
  overlayUrl,
  onUpload,
  onDelete,
  isLoading,
}: {
  overlayUrl: string | null;
  onUpload: (base64: string) => Promise<void>;
  onDelete: () => Promise<void>;
  isLoading: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        await onUpload(reader.result as string);
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const busy = isLoading || uploading;

  return (
    <div>
      <h3 className="text-sm font-semibold text-white/70 mb-2">Overlay de compartilhamento</h3>
      <p className="text-xs text-white/40 mb-3">
        Imagem PNG (9:16) usada como overlay ao compartilhar rodadas.
      </p>

      {overlayUrl ? (
        <div className="flex items-center gap-3">
          <div className="w-[60px] rounded-lg overflow-hidden bg-white/5 shrink-0">
            <img src={overlayUrl} alt="Overlay" className="w-full" />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors disabled:opacity-50"
            >
              Trocar
            </button>
            <button
              onClick={async () => { setUploading(true); try { await onDelete(); } finally { setUploading(false); } }}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded-lg bg-coral/10 text-coral hover:bg-coral/20 transition-colors disabled:opacity-50"
            >
              Remover
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className="w-full py-3 rounded-xl border border-dashed border-white/20 text-sm text-white/50 hover:border-turquoise/40 hover:text-turquoise transition-colors disabled:opacity-50"
        >
          {busy ? 'Enviando...' : 'Enviar overlay'}
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
*/

// ============================================================================
// Bonus points (admin)
// ============================================================================

/**
 * Admin-only manager for bonus points. Each entry credits (or debits) points to
 * one player on a given date; they are added on top of the match-derived total,
 * so every mutation asks the parent to reload the ranking.
 */
function BonusesCard({
  ranking,
  onChange,
}: {
  ranking: RankingDetail;
  onChange: () => void | Promise<void>;
}) {
  const [bonuses, setBonuses] = useState<RankingBonus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<RankingBonus | null>(null);
  const [removing, setRemoving] = useState<RankingBonus | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Only players that take part in the season can receive bonuses.
  const eligiblePlayers = useMemo(
    () => ranking.players.filter((p) => p.status === 'active' || p.status === 'inactive'),
    [ranking.players]
  );

  const load = useCallback(async () => {
    try {
      const response = await api.rankings.bonuses(ranking.id);
      if (response.success && response.data) setBonuses(response.data);
    } catch (error) {
      console.error('Failed to load bonuses:', error);
    } finally {
      setIsLoading(false);
    }
  }, [ranking.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Totals and per-player history both change, so drop the memoized histories.
  const afterMutation = async () => {
    playerHistoryCache.clear();
    await load();
    await onChange();
  };

  const handleRemove = async () => {
    if (!removing) return;
    setIsRemoving(true);
    try {
      await api.rankings.deleteBonus(ranking.id, removing.id);
      setRemoving(null);
      await afterMutation();
    } catch (error) {
      console.error('Failed to remove bonus:', error);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <>
      <Card delay={0.16}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2">
            <Gift className="w-4 h-4" />
            Bonificações
          </h3>
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="text-xs text-turquoise hover:text-turquoise/80 transition-colors flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Adicionar
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
          </div>
        ) : bonuses.length === 0 ? (
          <p className="text-xs text-white/40 leading-snug">
            Nenhuma bonificação. Pontos adicionados aqui entram na classificação do jogador.
          </p>
        ) : (
          <div className="space-y-2">
            {bonuses.map((bonus) => (
              <div key={bonus.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{bonus.playerName || 'Jogador'}</p>
                  <p className="text-[11px] text-white/40 truncate">
                    {formatBonusDate(bonus.date)}
                    {bonus.reason ? ` · ${bonus.reason}` : ''}
                  </p>
                </div>
                <span className={`text-sm font-bold tabular-nums shrink-0 ${bonus.points > 0 ? 'text-turquoise' : 'text-coral'
                  }`}>
                  {bonus.points > 0 ? '+' : ''}{bonus.points}
                </span>
                <button
                  onClick={() => { setEditing(bonus); setShowForm(true); }}
                  className="p-1.5 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors shrink-0"
                  aria-label="Editar bonificação"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setRemoving(bonus)}
                  className="p-1.5 text-white/40 hover:text-coral hover:bg-coral/10 rounded-lg transition-colors shrink-0"
                  aria-label="Remover bonificação"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <AnimatePresence>
        {showForm && (
          <BonusFormModal
            key={editing?.id || 'new'}
            rankingId={ranking.id}
            players={eligiblePlayers}
            bonus={editing}
            onClose={() => setShowForm(false)}
            onSaved={afterMutation}
          />
        )}
      </AnimatePresence>

      <ConfirmModal
        isOpen={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={handleRemove}
        isLoading={isRemoving}
        title="Remover bonificação"
        message={
          removing
            ? `Remover ${removing.points > 0 ? '+' : ''}${removing.points} ponto(s) de ${removing.playerName || 'jogador'}? A classificação será recalculada.`
            : ''
        }
        confirmLabel="Remover"
      />
    </>
  );
}

/**
 * Add/edit form for a single bonus entry. `bonus === null` means "add".
 * Mounted only while open (and keyed by entry), so the fields seed themselves
 * from the edited entry without a reset effect.
 */
function BonusFormModal({
  rankingId,
  players,
  bonus,
  onClose,
  onSaved,
}: {
  rankingId: string;
  players: RankingPlayer[];
  bonus: RankingBonus | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [playerId, setPlayerId] = useState(bonus?.playerId || '');
  const [points, setPoints] = useState(bonus ? String(bonus.points) : '');
  const [date, setDate] = useState(
    () => bonus?.date || new Date().toISOString().slice(0, 10)
  );
  const [reason, setReason] = useState(bonus?.reason || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const parsedPoints = parseInt(points, 10);
    if (!playerId) return setError('Selecione um jogador');
    if (!date) return setError('Informe a data');
    if (isNaN(parsedPoints) || parsedPoints === 0) return setError('Informe uma pontuação diferente de zero');

    setError(null);
    setIsSaving(true);
    try {
      const payload = { playerId, points: parsedPoints, date, reason: reason.trim() || null };
      const response = bonus
        ? await api.rankings.updateBonus(rankingId, bonus.id, payload)
        : await api.rankings.addBonus(rankingId, payload);

      if (response.success) {
        await onSaved();
        onClose();
      } else {
        setError(response.error?.message || 'Não foi possível salvar');
      }
    } catch (err) {
      console.error('Failed to save bonus:', err);
      setError('Não foi possível salvar');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm glass-card rounded-2xl p-6"
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-display text-xl font-bold text-white">
            {bonus ? 'Editar bonificação' : 'Nova bonificação'}
          </h3>
          <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3 mb-6">
          <div>
            <label className="block text-xs text-white/50 mb-1.5">Jogador</label>
            <select
              value={playerId}
              onChange={(e) => setPlayerId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white text-sm focus:outline-none focus:border-turquoise/50"
            >
              <option value="" className="bg-deep-blue">Selecione...</option>
              {players.map((p) => (
                <option key={p.id} value={p.id} className="bg-deep-blue">
                  {p.name || 'Jogador'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1.5">Data</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white text-sm focus:outline-none focus:border-turquoise/50"
            />
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1.5">Pontos</label>
            <input
              type="text"
              inputMode="numeric"
              value={points}
              placeholder="Ex.: 5 (use -5 para descontar)"
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || v === '-' || /^-?\d+$/.test(v)) setPoints(v);
              }}
              className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-turquoise/50"
            />
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1.5">Motivo (opcional)</label>
            <input
              type="text"
              value={reason}
              maxLength={200}
              placeholder="Ex.: organizou a rodada"
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-turquoise/50"
            />
          </div>

          {error && <p className="text-xs text-coral">{error}</p>}
        </div>

        <Button onClick={handleSave} fullWidth isLoading={isSaving}>
          Salvar
        </Button>
      </motion.div>
    </motion.div>
  );
}

function FormatDisplay({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex items-center justify-between p-2 rounded-lg bg-white/5">
      <span className="text-white/50">{label}</span>
      <span className="font-bold tabular-nums text-white">{value}</span>
    </div>
  );
}

// ============================================================================
// Shared Components
// ============================================================================

function PlayerAvatar({ player, size = 'md' }: { player: RankingPlayer; size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  return (
    <div className={`${sizeClass} rounded-full bg-gradient-to-br from-turquoise/30 to-coral/30 flex items-center justify-center text-white font-bold shrink-0`}>
      {player.avatarUrl ? (
        <img src={player.avatarUrl} alt="" className={`${sizeClass} rounded-full object-cover`} />
      ) : (
        player.name?.charAt(0) || '?'
      )}
    </div>
  );
}

function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  title,
  message,
  confirmLabel,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  title: string;
  message: string;
  confirmLabel: string;
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm glass-card rounded-2xl p-6"
          >
            <h3 className="font-display text-lg font-bold text-white mb-2">{title}</h3>
            <p className="text-white/60 text-sm mb-6">{message}</p>
            <div className="flex gap-3">
              <Button onClick={onClose} variant="secondary" fullWidth>Cancelar</Button>
              <Button onClick={onConfirm} isLoading={isLoading} fullWidth className="!bg-coral !text-white hover:!bg-coral/80">
                {confirmLabel}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// Scoring Edit Modal
// ============================================================================

function ScoringEditModal({
  isOpen,
  onClose,
  ranking,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  ranking: RankingDetail;
  onSaved: () => void;
}) {
  const [scoring, setScoring] = useState<ScoringRules>(ranking.scoringRules);
  const [totalRounds, setTotalRounds] = useState(
    ranking.totalRounds ? String(ranking.totalRounds) : ''
  );
  const [discardRounds, setDiscardRounds] = useState(
    ranking.discardRounds ? String(ranking.discardRounds) : ''
  );
  const [isSaving, setIsSaving] = useState(false);

  // Clearing "total de rodadas" turns the discard rule off entirely.
  const parsedTotalRounds = totalRounds.trim() ? parseInt(totalRounds, 10) : null;
  const parsedDiscardRounds =
    parsedTotalRounds !== null && discardRounds.trim() ? parseInt(discardRounds, 10) : 0;
  const formatError =
    parsedTotalRounds !== null &&
      (parsedTotalRounds < 1 || parsedTotalRounds > RANKING_LIMITS.MAX_ROUNDS_PER_SEASON)
      ? `O total de rodadas deve estar entre 1 e ${RANKING_LIMITS.MAX_ROUNDS_PER_SEASON}`
      : parsedTotalRounds !== null && parsedDiscardRounds >= parsedTotalRounds
        ? 'Os descartes devem ser menos que o total de rodadas'
        : null;

  const handleSave = async () => {
    if (formatError) return;
    setIsSaving(true);
    try {
      const response = await api.rankings.update(ranking.id, {
        scoringRules: scoring,
        totalRounds: parsedTotalRounds,
        discardRounds: parsedDiscardRounds,
      });
      if (response.success) {
        onSaved();
        onClose();
      }
    } catch (error) {
      console.error('Failed to update scoring:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm glass-card rounded-2xl p-6 max-h-[85vh] overflow-y-auto"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display text-xl font-bold text-white">Pontuação e formato</h3>
              <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 mb-6">
              <ScoringRulesFields
                value={scoring}
                onChange={(patch) => setScoring((prev) => ({ ...prev, ...patch }))}
              />

              <div className="pt-3 mt-1 border-t border-white/10 space-y-3">
                <p className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                  Formato da temporada
                </p>
                <RoundsEditInput
                  label="Total de rodadas"
                  value={totalRounds}
                  onChange={setTotalRounds}
                  placeholder="—"
                />
                <RoundsEditInput
                  label="Rodadas de descarte"
                  value={discardRounds}
                  onChange={setDiscardRounds}
                  placeholder="0"
                  disabled={!totalRounds.trim()}
                />
                <p className="text-[11px] text-white/40 leading-snug">
                  {parsedTotalRounds !== null && parsedDiscardRounds > 0
                    ? `Cada jogador soma apenas suas ${parsedTotalRounds - parsedDiscardRounds} melhores rodadas. Rodadas não jogadas contam como descarte.`
                    : 'Deixe o total em branco para que todas as rodadas contem, sem descartes.'}
                </p>
              </div>
            </div>

            <p className="text-[11px] text-white/40 mb-4 leading-snug">
              Ao salvar, os pontos de todas as rodadas são recalculados com as novas regras.
            </p>

            {formatError && <p className="text-coral text-sm mb-4">{formatError}</p>}

            <Button onClick={handleSave} fullWidth isLoading={isSaving} disabled={!!formatError}>
              Salvar
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function RoundsEditInput({
  label,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${disabled ? 'text-white/30' : 'text-white/60'}`}>{label}</span>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '' || /^\d{1,3}$/.test(v)) onChange(v);
        }}
        className="w-20 px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 text-white text-center text-sm placeholder-white/25 focus:outline-none focus:border-turquoise/50 disabled:opacity-40"
      />
    </div>
  );
}

// ============================================================================
// Activation Window Modal
// ============================================================================

/**
 * The form lives in a child that only mounts while the modal is open, so its
 * useState initialisers re-seed from the server on every open — no syncing effect.
 */
function ActivationEditModal({
  isOpen,
  onClose,
  ranking,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  ranking: RankingDetail;
  onSaved: () => void;
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <ActivationEditForm ranking={ranking} onClose={onClose} onSaved={onSaved} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ActivationEditForm({
  ranking,
  onClose,
  onSaved,
}: {
  ranking: RankingDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState(ranking.activation.mode);
  const [weekday, setWeekday] = useState(ranking.activation.weekday ?? 6);
  const [time, setTime] = useState(ranking.activation.time ?? '12:00');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await api.rankings.update(ranking.id, {
        activation:
          mode === 'scheduled'
            ? {
              mode,
              weekday,
              time,
              // Anchor the window to the device's timezone — for this audience it
              // is the same zone the ranking is played in.
              timezone: deviceTimeZone(ranking.activation.timezone),
            }
            : { mode },
      });
      if (response.success) {
        onSaved();
        onClose();
      } else {
        setError(response.error?.message || 'Erro ao salvar');
      }
    } catch (err) {
      console.error('Failed to update activation settings:', err);
      setError('Erro ao salvar');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-sm glass-card rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
    >
      <div className="flex justify-between items-start mb-4">
        <h3 className="font-display text-xl font-bold text-white">Inscrições</h3>
        <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      <p className="text-sm text-white/60 mb-4">
        Quando os jogadores voltam para inativo e precisam confirmar presença na próxima rodada.
      </p>

      <div className="space-y-2 mb-4">
        <ScheduleModeOption
          selected={mode === 'round_end'}
          onSelect={() => setMode('round_end')}
          title="Ao encerrar a rodada"
          description="Todos voltam para inativo quando a rodada atual termina."
        />
        <ScheduleModeOption
          selected={mode === 'scheduled'}
          onSelect={() => setMode('scheduled')}
          title="Em dia e horário fixos"
          description="Toda semana, no horário escolhido — independente da rodada atual."
        />
      </div>

      {mode === 'scheduled' && (
        <div className="mb-6 pt-4 border-t border-white/10">
          <WeeklyScheduleFields
            weekday={weekday}
            time={time}
            onWeekdayChange={setWeekday}
            onTimeChange={setTime}
          />

          <p className="text-[11px] text-white/40 mt-3 leading-snug">
            A rodada em andamento continua normalmente — os jogos já sorteados seguem
            valendo mesmo para quem não se inscrever na próxima.
          </p>
        </div>
      )}

      {error && <p className="text-coral text-sm mb-4">{error}</p>}

      <Button onClick={handleSave} fullWidth isLoading={isSaving}>
        Salvar
      </Button>
    </motion.div>
  );
}

// ============================================================================
// New Season Modal
// ============================================================================

function NewSeasonModal({
  isOpen,
  onClose,
  rankingId,
}: {
  isOpen: boolean;
  onClose: () => void;
  rankingId: string;
}) {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);

    try {
      const response = await api.rankings.newSeason(rankingId);
      if (response.success && response.data) {
        onClose();
        navigate(`/rankings/${response.data.id}`);
      } else {
        setError(response.error?.message || 'Erro ao criar temporada');
      }
    } catch {
      setError('Erro ao criar temporada');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm glass-card rounded-2xl p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display text-xl font-bold text-white">Nova temporada</h3>
              <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-white/60 mb-4">
              Jogadores, configuração de pontuação e descrição serão copiados para a nova temporada e podem ser editados depois. Rodadas e pontuações começarão do zero.
            </p>

            {error && <p className="text-coral text-sm mb-4">{error}</p>}

            <Button onClick={handleCreate} fullWidth isLoading={isCreating}>
              Criar temporada
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// Player History Modal
// ============================================================================

function PlayerHistoryModal({
  rankingId,
  player,
  partialDiscards,
  onClose,
}: {
  rankingId: string;
  player: RankingPlayer;
  /** "Considerar descartes" is on: also grey out the rounds that view drops. */
  partialDiscards: boolean;
  onClose: () => void;
}) {
  const cacheKey = `${rankingId}:${player.id}`;
  const isDiscarded = (entry: PlayerHistoryEntry) =>
    entry.discarded || (partialDiscards && !!entry.partiallyDiscarded);
  const cached = playerHistoryCache.get(cacheKey);
  const [history, setHistory] = useState<PlayerHistoryEntry[]>(cached?.entries || []);
  const [bonuses, setBonuses] = useState<RankingBonus[]>(cached?.bonuses || []);
  const [isLoading, setIsLoading] = useState(!cached);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.rankings.playerHistory(rankingId, player.id);
        if (response.success && response.data) {
          playerHistoryCache.set(cacheKey, response.data);
          setHistory(response.data.entries);
          setBonuses(response.data.bonuses);
        }
      } catch (error) {
        console.error('Failed to load player history:', error);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [rankingId, player.id, cacheKey]);

  const resultConfig = {
    win: { label: 'V', className: 'bg-green-500/20 text-green-400' },
    loss: { label: 'D', className: 'bg-coral/20 text-coral' },
    wo: { label: 'WO', className: 'bg-sunny/20 text-sunny' },
    no_points: { label: 'SP', className: 'bg-white/10 text-white/40' },
  };

  // Group entries by round number
  const groupedByRound = useMemo(() => {
    const map = new Map<number, PlayerHistoryEntry[]>();
    for (const entry of history) {
      if (!map.has(entry.roundNumber)) map.set(entry.roundNumber, []);
      map.get(entry.roundNumber)!.push(entry);
    }
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [history]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm glass-card rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-white/10">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-turquoise/30 to-coral/30 flex items-center justify-center text-white font-bold text-sm shrink-0">
            {player.avatarUrl ? (
              <img src={player.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              player.name?.charAt(0) || '?'
            )}
          </div>
          <h3 className="font-display text-lg font-bold text-white flex-1 truncate">
            {player.name || 'Jogador'}
          </h3>
          <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-white/30 animate-spin" />
            </div>
          ) : (
            <>
              {/* Bonus points come first — they sit outside the round history */}
              {bonuses.length > 0 && (
                <div className="border-b border-white/10">
                  <div className="px-4 py-2 bg-sunny/10">
                    <p className="text-[11px] font-semibold text-sunny uppercase tracking-wider flex items-center gap-1.5">
                      <Gift className="w-3 h-3" />
                      Bonificações
                    </p>
                  </div>
                  {bonuses.map((bonus) => (
                    <div key={bonus.id} className="px-4 py-3 flex items-center gap-3">
                      <span className="w-7 text-center text-[10px] font-bold px-1.5 py-0.5 rounded bg-sunny/20 text-sunny shrink-0">
                        B
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/50">{formatBonusDate(bonus.date)}</p>
                        {bonus.reason && (
                          <p className="text-xs text-white/30 truncate">{bonus.reason}</p>
                        )}
                      </div>
                      <span className={`text-sm font-bold tabular-nums shrink-0 ${bonus.points > 0 ? 'text-green-400' : 'text-coral'
                        }`}>
                        {bonus.points > 0 ? '+' : ''}{bonus.points}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {history.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <History className="w-8 h-8 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">Nenhuma partida registrada.</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {groupedByRound.map(([roundNumber, entries]) => {
                    // The discard unit is the group: a round with a regular and
                    // a catch-up group can lose one of them and keep the other.
                    const roundDiscarded = entries.every(isDiscarded);
                    const discardedEntries = entries.filter(isDiscarded);
                    const discardedGroups = new Set(discardedEntries.map((e) => e.groupNumber)).size;
                    const discardedPoints = discardedEntries.reduce((sum, e) => sum + e.points, 0);
                    // Dropped only by the toggle's view, not by the season rule.
                    const temporary = !discardedEntries.some((e) => e.discarded);
                    return (
                      <div key={roundNumber}>
                        <div className="px-4 py-2 bg-white/5 flex items-center justify-between gap-2">
                          <p className="text-[11px] font-semibold text-white/50 uppercase tracking-wider">
                            Rodada {roundNumber}
                          </p>
                          {discardedGroups > 0 && (
                            <span className="text-[10px] font-semibold text-white/40 bg-white/10 px-1.5 py-0.5 rounded">
                              {roundDiscarded
                                ? temporary ? 'Temporariamente descartada' : 'Descartada'
                                : `${discardedGroups} grupo${discardedGroups === 1 ? '' : 's'} descartado${discardedGroups === 1 ? '' : 's'}${temporary ? ' temporariamente' : ''}`}{' '}
                              ({discardedPoints > 0 ? '+' : ''}{discardedPoints})
                            </span>
                          )}
                        </div>
                        {entries.map((entry, i) => {
                          const rc = resultConfig[entry.result];
                          return (
                            <div
                              key={`${roundNumber}-${i}`}
                              className={`px-4 py-3 flex items-center gap-3 ${isDiscarded(entry) ? 'opacity-40' : ''}`}
                            >
                              {/* Result badge */}
                              <span className={`w-7 text-center text-[10px] font-bold px-1.5 py-0.5 rounded ${rc.className}`}>
                                {rc.label}
                              </span>

                              {/* Score */}
                              <div className="w-10 text-center shrink-0">
                                {entry.teamGames !== null && entry.opponentGames !== null ? (
                                  <p className="text-sm font-bold text-white tabular-nums">
                                    {entry.teamGames}-{entry.opponentGames}
                                  </p>
                                ) : (
                                  <p className="text-sm text-white/30">—</p>
                                )}
                              </div>

                              {/* Partner & Opponents */}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-white/50 truncate">
                                  c/ {entry.partner.name || '?'}
                                </p>
                                <p className="text-xs text-white/30 truncate">
                                  vs {entry.opponents[0].name || '?'} e {entry.opponents[1].name || '?'}
                                </p>
                              </div>

                              {/* Points */}
                              <span className={`text-sm font-bold tabular-nums shrink-0 ${isDiscarded(entry) ? 'text-white/40 line-through'
                                : entry.points > 0 ? 'text-green-400'
                                  : entry.points < 0 ? 'text-coral' : 'text-white/30'
                                }`}>
                                {entry.points > 0 ? '+' : ''}{entry.points}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================================================
// Payment Screen (pending_payment players — BT Vision-branded PIX checkout)
// ============================================================================

function PaymentScreen({ rankingId, onPaid }: { rankingId: string; onPaid: () => void | Promise<void> }) {
  const [invoice, setInvoice] = useState<MyInvoice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaid, setIsPaid] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // Backs off 5s → 40s. A PIX is usually ready within seconds, so stay eager
    // early; past that the player is parked on this screen and a tight loop
    // just burns their data and our rate limit.
    let delay = 5000;
    const MAX_DELAY = 40000;

    const poll = async () => {
      try {
        const response = await api.rankings.myInvoice(rankingId);
        if (cancelled) return;
        if (response.success && response.data) {
          setInvoice(response.data.invoice);
          if (response.data.myStatus !== 'pending_payment' || response.data.invoice?.status === 'paid') {
            setIsPaid(true);
            // Give the success animation a beat, then reload the full ranking.
            setTimeout(() => void onPaid(), 1600);
            return;
          }
          // The backend gave up creating the charge. Nothing will change until
          // the organizer fixes their account, so stop asking.
          if (response.data.invoice?.creationFailed) return;
        }
      } catch {
        // keep polling — transient network errors are fine here
      } finally {
        if (!cancelled) setIsLoading(false);
      }
      timer = setTimeout(poll, delay);
      delay = Math.min(delay * 2, MAX_DELAY);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [rankingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyPayload = () => {
    if (!invoice?.pixPayload) return;
    navigator.clipboard.writeText(invoice.pixPayload);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isPaid) {
    return (
      <Card delay={0.05}>
        <div className="flex flex-col items-center text-center py-10">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.1 }}
            className="w-16 h-16 rounded-full bg-green-400/10 flex items-center justify-center mb-4"
          >
            <Check className="w-8 h-8 text-green-400" />
          </motion.div>
          <h2 className="text-lg font-semibold text-white mb-2">Pagamento confirmado!</h2>
          <p className="text-sm text-white/50">Liberando seu acesso ao ranking…</p>
        </div>
      </Card>
    );
  }

  if (isLoading && !invoice) {
    return (
      <Card delay={0.05}>
        <div className="flex flex-col items-center py-12">
          <Loader2 className="w-8 h-8 text-turquoise animate-spin" />
        </div>
      </Card>
    );
  }

  // Creation was abandoned: the organizer's payment account is not able to
  // receive PIX. Say so plainly instead of spinning forever — the player can
  // do nothing here except talk to whoever runs the ranking.
  if (invoice?.creationFailed) {
    return (
      <Card delay={0.05}>
        <div className="flex flex-col items-center text-center py-10">
          <div className="w-14 h-14 rounded-full bg-warning-orange/10 flex items-center justify-center mb-4">
            <AlertCircle className="w-7 h-7 text-warning-orange" />
          </div>
          <h2 className="text-lg font-semibold text-white mb-2">
            Não foi possível gerar sua cobrança
          </h2>
          <p className="text-sm text-white/50 max-w-xs">
            Entre em contato com o organizador para ele regularizar a conta de
            pagamento do ranking. Sua vaga está guardada.
          </p>
        </div>
      </Card>
    );
  }

  if (!invoice || invoice.status === 'pending_creation') {
    return (
      <Card delay={0.05}>
        <div className="flex flex-col items-center text-center py-10">
          <Loader2 className="w-8 h-8 text-turquoise animate-spin mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">Gerando cobrança…</h2>
          <p className="text-sm text-white/50 max-w-xs">
            Estamos preparando seu PIX. Isso pode levar alguns minutos — volte em breve
            ou aguarde nesta tela.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card delay={0.05}>
      <div className="flex flex-col items-center text-center py-4">
        {invoice.status === 'overdue' && (
          <div className="w-full p-3 rounded-xl bg-coral/10 border border-coral/20 mb-4">
            <p className="text-coral text-sm font-medium">
              Sua cobrança venceu, mas o PIX continua válido. Pague para liberar o acesso.
            </p>
          </div>
        )}

        <h2 className="text-lg font-semibold text-white mb-1">Pague sua inscrição</h2>
        <p className="text-2xl font-bold text-turquoise mb-1">{formatBRLCents(invoice.amountCents)}</p>
        {invoice.dueDate && (
          <p className="text-xs text-white/40 mb-4">
            Vencimento: {formatBonusDate(invoice.dueDate)}
          </p>
        )}

        {invoice.pixQrBase64 && (
          <div className="p-3 bg-white rounded-2xl mb-4">
            <img
              src={`data:image/png;base64,${invoice.pixQrBase64}`}
              alt="QR Code PIX"
              className="w-48 h-48"
            />
          </div>
        )}

        {invoice.pixPayload && (
          <div className="w-full">
            <p className="text-xs text-white/40 mb-2">Ou copie o código PIX:</p>
            <button
              onClick={copyPayload}
              className="w-full flex items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-turquoise/40 transition-colors"
            >
              <p className="flex-1 font-mono text-[11px] text-white/60 truncate text-left">
                {invoice.pixPayload}
              </p>
              {copied ? (
                <Check className="w-4 h-4 text-green-400 shrink-0" />
              ) : (
                <Copy className="w-4 h-4 text-white/40 shrink-0" />
              )}
            </button>
            {copied && <p className="text-green-400 text-xs mt-2">Copiado!</p>}
          </div>
        )}

        <p className="text-xs text-white/40 mt-4 max-w-xs leading-snug">
          Assim que o pagamento for confirmado, seu acesso é liberado automaticamente.
        </p>
      </div>
    </Card>
  );
}

// ============================================================================
// Payment Edit Modal (amount + due-days; enabling is creation-only)
// ============================================================================

function PaymentEditModal({
  isOpen,
  onClose,
  ranking,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  ranking: RankingDetail;
  onSaved: () => void | Promise<void>;
}) {
  const [amount, setAmount] = useState(
    ranking.paymentInfo?.amountCents ? String(ranking.paymentInfo.amountCents / 100) : ''
  );
  const [dueDays, setDueDays] = useState(
    ranking.paymentInfo?.dueDays ? String(ranking.paymentInfo.dueDays) : '3'
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const amountCents = Math.round(parseFloat(amount.replace(',', '.')) * 100);
    const parsedDueDays = parseInt(dueDays, 10);
    if (!Number.isFinite(amountCents) || amountCents < 500) {
      setError('Valor mínimo: R$ 5,00');
      return;
    }
    if (!Number.isInteger(parsedDueDays) || parsedDueDays < 1 || parsedDueDays > 30) {
      setError('Prazo deve ser de 1 a 30 dias');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const response = await api.rankings.update(ranking.id, {
        payment: { amountCents, dueDays: parsedDueDays },
      });
      if (response.success) {
        await onSaved();
        onClose();
      } else {
        setError(response.error?.message || 'Erro ao salvar');
      }
    } catch {
      setError('Erro de conexão');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm glass-card rounded-2xl p-6"
          >
            <h3 className="font-display text-lg font-bold text-white mb-4">Inscrição paga</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-xs text-white/50 mb-1.5">Valor (R$)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-turquoise/50"
                />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1.5">Prazo de pagamento (dias)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={dueDays}
                  onChange={(e) => setDueDays(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-turquoise/50"
                />
              </div>
              <p className="text-xs text-white/40 leading-snug">
                Vale apenas para cobranças futuras — cobranças já geradas não mudam.
                {ranking.paymentInfo?.platformFeePercent
                  ? ` O BT Vision retém ${ranking.paymentInfo.platformFeePercent}% de taxa de serviço sobre cada inscrição.`
                  : ''}
              </p>
              {error && <p className="text-coral text-xs">{error}</p>}
            </div>
            <div className="flex gap-3">
              <Button onClick={onClose} variant="secondary" fullWidth>Cancelar</Button>
              <Button onClick={handleSave} isLoading={isSaving} fullWidth>Salvar</Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
